/**
 * Registro de modalidades.
 *
 * O resto do sistema — servidor, lobby, interface — fala SÓ com esta
 * interface. Nada fora daqui precisa saber se a partida é 8-Ball ou sinuca
 * brasileira, e acrescentar uma terceira modalidade não deve exigir mexer em
 * nenhum deles.
 *
 * O preço dessa indireção é que os tipos ficam genéricos (`unknown` no estado
 * concreto). Vale a pena: sem ela, cada modalidade nova espalharia um `if` por
 * todo o servidor, e é assim que um projeto fica impossível de manter.
 */

import * as eightball from './eightball/rules'
import * as eightballTypes from './eightball/types'
import * as sinuca from './sinuca/rules'
import * as sinucaTypes from './sinuca/types'
import type { EpocaDasRegras } from './eightball/types'

export type GameModeId = 'eightball' | 'sinuca'

export const GAME_MODES: readonly GameModeId[] = ['eightball', 'sinuca']

/** Metadados para a interface mostrar as opções sem conhecer as regras. */
export type GameModeInfo = {
  id: GameModeId
  name: string
  description: string
  /** Quantas bolas, sem contar a branca. */
  ballCount: number
}

export const GAME_MODE_INFO: Record<GameModeId, GameModeInfo> = {
  eightball: {
    id: 'eightball',
    name: '8-Ball',
    description: 'Lisas contra listradas. Quem limpar o grupo e encaçapar a 8 vence.',
    ballCount: 15,
  },
  sinuca: {
    id: 'sinuca',
    name: 'Sinuca brasileira',
    description: 'Sete coloridas em ordem crescente. Vence quem fizer mais pontos.',
    ballCount: 7,
  },
}

/**
 * Contrato que toda modalidade cumpre.
 *
 * `TState` e `TOutcome` ficam genéricos porque cada jogo tem os seus; quem
 * chama trata como opaco e só repassa entre as funções.
 */
export type GameMode<TState = unknown, TOutcome = unknown, TRuling = unknown> = {
  info: GameModeInfo
  /**
   * Estado inicial de uma partida.
   *
   * `epoca` só é passada por quem REPRODUZ uma partida antiga: as regras
   * mudam, e um replay gravado antes de uma mudança precisa ser julgado pelas
   * regras de quando foi jogado. Quem está jogando agora omite, e recebe as
   * regras correntes.
   */
  create(breaker: 0 | 1, epoca?: EpocaDasRegras): TState
  /** Julga a tacada sem alterar nada. */
  judge(state: TState, outcome: TOutcome): TRuling
  /** Julga e avança para o estado da próxima tacada. */
  play(state: TState, outcome: TOutcome): { state: TState; ruling: TRuling }
  /**
   * Decisão que precisa ser tomada antes da próxima tacada, se houver.
   *
   * Devolve `null` na modalidade que não tem nenhuma. Quem chama DEVE consultar
   * isto entre tacadas: mandar jogar com decisão pendente é erro, e as regras
   * lançam em vez de escolher sozinhas — escolher seria tirar do jogador um
   * direito que a regra lhe dá.
   */
  pendingOf(state: TState): PendingDecision | null
  /**
   * O jogador da vez pode reposicionar a branca?
   *
   * `null` quando não; a região quando sim. Precisa estar no contrato comum
   * porque quem coloca a bola é a interface, e ela não conhece o estado
   * concreto de nenhuma modalidade.
   *
   * Isto também decide o que vai para o replay: uma posição escolhida pelo
   * jogador é ENTRADA, e entrada não gravada faz a verificação reproduzir
   * outra partida.
   */
  ballInHandOf(state: TState): BallInHandRegion | null
  /**
   * A mesa JÁ foi quebrada? Se não, a próxima tacada é a quebra.
   *
   * Está no contrato comum porque a resposta decide a potência da tacada (o
   * bônus de quebra do taco) e a régua de força da interface — e quem pergunta
   * não conhece o estado concreto de nenhuma modalidade.
   *
   * Por que não CONTAR TACADAS, que era o critério antigo: a contagem nunca
   * volta a zero, então o re-rack (quebra ilegal, ou 8 na quebra) não era visto
   * como quebra. Quem ganhava o direito de quebrar de novo perdia o bônus e
   * ficava com a régua cortada em 7/12 — bem mais fraco que na primeira quebra.
   *
   * E por que não perguntar "a mesa ainda está cheia?": uma quebra que não
   * encaçapa nada deixa a mesa cheia e a tacada seguinte NÃO é quebra. `broken`
   * acerta os dois casos porque vira verdadeiro em toda tacada aplicada,
   * encaçape ou não, e volta a falso só quando as regras armam um rack novo.
   */
  mesaQuebrada(state: TState): boolean
  /**
   * O jogador da vez precisa declarar bola e caçapa nesta tacada?
   *
   * A WPA exige na bola 8: dizer ANTES em qual buraco ela vai cair, para
   * ninguém ganhar de sorte. Sem declarar, encaçapar a 8 é falta — e falta na
   * 8 é DERROTA.
   *
   * Precisa estar no contrato comum porque quem declara é a interface, e ela
   * não conhece o estado concreto de nenhuma modalidade.
   */
  callRequiredOf(state: TState): boolean
  /**
   * A bola que esta tacada é OBRIGADA a acertar primeiro, se a modalidade
   * impuser uma.
   *
   * `null` quando não há uma só — no 8-Ball o alvo é um grupo inteiro, e
   * apontar uma bola dele seria mentir para o jogador. A sinuca impõe a menor
   * da mesa, e sem isto a interface não tem como dizer qual é: as sete
   * coloridas parecem igualmente jogáveis.
   *
   * Fica no contrato comum porque quem mostra é a interface, que não conhece o
   * estado concreto de nenhuma modalidade.
   */
  targetBallOf(state: TState): number | null
  /**
   * Bater PRIMEIRO nesta bola seria falta?
   *
   * Existe para a tela marcar a previsão antes de o jogador tacar. Cada
   * modalidade responde com a MESMA função que usa para julgar a tacada: se
   * respondesse com uma cópia, o aviso e o juiz divergiriam na primeira
   * mudança de regra, e o jogador aprenderia a confiar no aviso errado.
   */
  contatoSeriaFalta(state: TState, bola: number): boolean
  /** Aplica a escolha do jogador e libera a próxima tacada. */
  resolve(state: TState, optionIndex: number, epoca?: EpocaDasRegras): DecisionOutcome<TState>
  /** Desistência ou W.O. por tempo. */
  forfeit(state: TState, quemDesiste: 0 | 1): TState
  /** Quem venceu, se a partida acabou. */
  winnerOf(state: TState): 0 | 1 | null
  /**
   * Quem está na frente AGORA, se a partida tivesse de acabar aqui.
   *
   * `null` quando ninguém está — e aí não há como decidir sem inventar.
   *
   * Existe por causa de um free-roll: o replay tem teto de tacadas, e ao
   * estourar a partida era ANULADA com reembolso. Quem estava perdendo então
   * simplesmente parava de jogar — cada prazo estourado consome uma tacada
   * nula, e em 37 minutos o teto chegava e o dinheiro voltava. O mesmo ataque
   * do prazo do escrow, por outra porta.
   *
   * Decidir pelo estado remove o incentivo: parar de jogar não pontua, e quem
   * não pontua perde.
   */
  standingWinner(state: TState): 0 | 1 | null
  /** Resumo para a interface, sem ela conhecer o formato interno. */
  summarize(state: TState): MatchSummary
}

/**
 * Escolha aberta entre tacadas, no vocabulário comum.
 *
 * As opções vêm como índices para caberem num byte do replay. O texto serve à
 * interface; o índice é o que fica gravado e o que a verificação reproduz.
 * O ÍNDICE canônico de cada opção (`BREAK_CHOICES`) é parte do formato:
 * trocá-lo faria replays antigos reproduzirem outra escolha. A ORDEM desta
 * lista não é: ela é a ordem da tela e a escolha do relógio
 * (`OPCOES_POR_SITUACAO`).
 */
export type PendingDecision = {
  chooser: 0 | 1
  /** Identificador estável da situação, para a interface explicar. */
  kind: string
  /**
   * As opções VÁLIDAS para esta situação, cada uma com o índice canônico.
   *
   * O índice é o que fica gravado no replay, e a lista canônica não pode ser
   * reordenada nem filtrada — por isso ele viaja junto do rótulo em vez de ser
   * a posição no array.
   *
   * Antes disto a interface oferecia as quatro opções nas duas situações, e
   * três delas mentiam: escolher "Aceitar a mesa" na 8 da quebra rearmava o
   * rack, porque `resolveChoice` manda tudo que não é `respot-eight` para o
   * `default`.
   */
  options: readonly { index: number; label: string }[]
}

/**
 * Onde a branca pode ser colocada.
 *
 * `kitchen` é a área atrás da linha da cabeça, para onde a WPA manda a bola
 * depois de falta na quebra. `anywhere` é qualquer ponto livre da mesa.
 */
export type BallInHandRegion = 'anywhere' | 'kitchen'

export type DecisionOutcome<TState> = {
  state: TState
  /**
   * Bolas que a escolha manda de volta ao ponto de pé.
   *
   * Pelo mesmo motivo de `rerack`: o estado de REGRAS e o da MESA são
   * separados, e as regras não movem bola nenhuma. Sem isto, escolher
   * "recolocar a 8" tirava a 8 da lista de encaçapadas mas a deixava fora da
   * mesa física — e quem limpasse o grupo nunca mais conseguia tocá-la. Toda
   * tacada virava falta por falta de contato, para sempre.
   */
  respot: number[]
  /**
   * A escolha exige montar o triângulo de novo.
   *
   * Precisa ser explícito porque o estado de REGRAS e o estado FÍSICO da mesa
   * são separados: as regras zeram as bolas encaçapadas, mas as bolas em si
   * continuam onde pararam até alguém rearmar. Inferir isso do estado seria
   * frágil, e errar significa jogo e verificador divergirem.
   */
  rerack: boolean
}

/**
 * Visão da partida que serve para qualquer modalidade.
 *
 * É o que o placar da interface consome. Cada jogo preenche do seu jeito: o
 * 8-Ball em bolas restantes, a sinuca em pontos.
 */
/**
 * O que está em jogo agora, como CÓDIGO.
 *
 * É o que a interface traduz no idioma do jogador; o `status` em texto fica
 * por compatibilidade (e para o log). Os parâmetros são números de propósito:
 * `bola` é o id da bola, não a cor dela — a cor é palavra, e palavra é do
 * dicionário do cliente.
 */
export type StatusCode =
  | 'quebra'
  | 'mesa_aberta'
  | 'na_bola_8'
  | 'faltam'
  | 'venceu'
  | 'mesa_vazia'
  | 'bola_da_vez'
  /**
   * A disputa da saída, antes da quebra (`disputa.ts`). Nenhuma modalidade o
   * emite: é a mesa da disputa quem o põe no resumo, para o placar e o topo da
   * tela dizerem onde a partida está. Parâmetros: `rodada` (a partir de 1) e
   * `total`.
   */
  | 'disputa'

export type Status = { code: StatusCode; params?: Record<string, number> }

export type MatchSummary = {
  turn: 0 | 1
  /** Texto curto do que está em jogo agora. Compatibilidade; prefira `statusCode`. */
  status: string
  /** O mesmo, como código traduzível. */
  statusCode: Status
  /** Placar, quando a modalidade tem um. */
  score: [number, number] | null
  /** Ids ainda na mesa. */
  onTable: number[]
  /**
   * As bolas que faltam a CADA jogador, por índice.
   *
   * `null` nas modalidades que não dividem a mesa — a sinuca não tem grupo.
   *
   * Está no vocabulário comum, e não escondido no estado do 8-Ball, porque é
   * informação que o jogador precisa ter na tela o tempo todo: sem ela dá para
   * jogar a partida inteira sem saber se você é das lisas ou das listradas, e
   * descobrir errado custa a vez.
   */
  suas: [number[], number[]] | null
  finished: boolean
  winner: 0 | 1 | null
  /**
   * POR QUE a partida acabou, como código (`MatchEnding.kind` do 8-Ball,
   * `SinucaEnding.kind` da sinuca), ou null enquanto ela corre.
   *
   * Sem isto a tela de derrota dizia só "por regras". Quem perde por regra
   * quer saber QUAL — e a página de regras tem uma âncora para cada uma.
   */
  ending: string | null
}

// ------------------------------------------------------- regras em vigor

/**
 * O conjunto de regras que CADA modalidade joga de fato.
 *
 * Antes isto era implícito: o modo chamava `judgeShot(state, outcome)` e o
 * padrão do parâmetro decidia. Funcionava, mas ninguém de fora conseguia
 * responder "pelo que estou jogando?" sem ler o código das regras — e a página
 * de regras precisa mostrar exatamente o conjunto em uso, não uma cópia que
 * desatualiza. Aqui o modo passa o conjunto de propósito, e a página importa
 * o mesmo objeto. Trocar de regra é trocar UMA linha, e a página acompanha.
 */
export const ACTIVE_RULES = {
  // Desde a v8: a caçapa é declarada em toda tacada. Ver `POCKET_RULES`.
  eightball: eightballTypes.POCKET_RULES,
  sinuca: sinucaTypes.DEFAULT_SINUCA_RULES,
} as const satisfies Record<GameModeId, unknown>

/**
 * As regras de 8-Ball com que ESTA partida é julgada.
 *
 * As de hoje (`ACTIVE_RULES`), com uma exceção de época: partida criada antes
 * da v8 — e é o verificador reproduzindo um replay antigo que cria uma assim —
 * declara só na 8, como declarava quando foi jogada. Sem isto, um replay v7
 * pediria declarações que ele não tem, e a partida liquidada deixaria de se
 * provar.
 */
const regrasDaPartida = (state: eightballTypes.MatchState): eightballTypes.RuleSet =>
  state.cacapaEmTodaTacada ? ACTIVE_RULES.eightball : { ...ACTIVE_RULES.eightball, callShot: 'eight-only' }

// -------------------------------------------------------------- 8-Ball

/**
 * Ordem canônica das escolhas pós-quebra.
 *
 * NÃO REORDENAR. O índice nesta lista é o que vai gravado no replay; mudar a
 * ordem faria toda partida antiga ser reproduzida com outra escolha, e a
 * auditoria passaria a apontar vencedores errados sem nada acusar.
 */
const BREAK_CHOICES: readonly eightballTypes.BreakChoice[] = [
  'accept',
  'rerack-self',
  'rerack-opponent',
  'respot-eight',
]

const BREAK_CHOICES_LABELS: readonly string[] = [
  'Aceitar a mesa',
  'Quebrar de novo',
  'Devolver a quebra',
  'Recolocar a 8',
]

/**
 * Quais índices canônicos valem em cada situação.
 *
 * A WPA dá DUAS opções quando a 8 cai na quebra (4.3e) e TRÊS quando a quebra
 * é irregular (4.3d). Oferecer as quatro nas duas fazia o jogador escolher algo
 * que o motor de regras interpretava como outra coisa.
 */
const OPCOES_POR_SITUACAO: Record<string, readonly number[]> = {
  'eight-on-break': [3, 1], // recolocar a 8, ou quebrar de novo
  /*
   * Quebrar de novo, aceitar, devolver a quebra — NESTA ordem.
   *
   * A ordem diz duas coisas: a tela mostra nela, e o RELÓGIO escolhe a
   * primeira quando ninguém responde. Era "aceitar" primeiro; o Bunker decidiu
   * (2026-09-30) que é "quebrar de novo": aceitar uma quebra fraca deixa a
   * mesa travada, com o bolo ainda junto, e a partida perde o ritmo. Quebrando
   * de novo, quem não respondeu a tempo quebra com o bônus do taco — o
   * critério de quebra é a mesa estar armada (`mesaQuebrada`), não quem
   * decidiu.
   *
   * Mudar a ordem NÃO mexe em partida gravada: o replay guarda o índice
   * canônico (`BREAK_CHOICES`), e `resolve` só confere se ele é permitido aqui.
   */
  'illegal-break': [1, 0, 2],
}

/**
 * Quem ganha quando o estado não decide.
 *
 * `standingWinner` existe para fechar um free-roll: o replay tem teto de
 * tacadas, estourá-lo ANULAVA a partida com reembolso, e quem estava perdendo
 * simplesmente parava de jogar. Devolver `null` mantinha o buraco aberto
 * exatamente nos dois estados que o atacante busca — mesa aberta e contagem
 * igualada são o que se obtém parando de jogar cedo.
 *
 * O critério é "quem quebrou perde": a quebra é a vantagem de abrir a mesa, os
 * dois sabem quem quebrou desde antes da primeira tacada, e nada que aconteça
 * durante a partida muda o valor. Precisa ser determinístico e registrado
 * porque o resultado vai para a blockchain — não pode haver estado final sem
 * destinatário.
 */
const desempate = (breaker: 0 | 1): 0 | 1 => (breaker === 0 ? 1 : 0)

const eightballMode: GameMode<
  eightballTypes.MatchState,
  eightballTypes.ShotOutcome,
  eightballTypes.ShotRuling
> = {
  info: GAME_MODE_INFO.eightball,
  create: (breaker, epoca) => eightballTypes.createMatch(breaker, epoca),
  judge: (state, outcome) => eightball.judgeShot(state, outcome, regrasDaPartida(state)),
  play: (state, outcome) => eightball.playShot(state, outcome, regrasDaPartida(state)),
  pendingOf: (state) => {
    const p = state.pending
    if (!p) return null

    const validas = OPCOES_POR_SITUACAO[p.kind] ?? BREAK_CHOICES.map((_, i) => i)
    return {
      chooser: p.chooser,
      kind: p.kind,
      options: validas.map((index) => ({ index, label: BREAK_CHOICES_LABELS[index]! })),
    }
  },
  /*
   * Decisão pendente vem ANTES de posicionar a branca.
   *
   * Desde que a quebra irregular parou de apagar a falta da branca, as duas
   * coisas coexistem: o adversário tem bola na mão E a escolha de rearmar. Mas
   * rearmar descarta a bola na mão, então oferecer a colocação antes da escolha
   * deixaria o jogador posicionar uma branca que pode nunca ser usada — e o
   * replay grava posições ESCOLHIDAS, então uma posição descartada é uma
   * entrada gravada que o verificador tem de reproduzir sem efeito.
   *
   * O direito continua no estado; só não é oferecido ainda.
   */
  mesaQuebrada: (state) => state.broken,
  ballInHandOf: (state) => {
    if (state.pending) return null
    return state.ballInHand.active ? state.ballInHand.region : null
  },
  callRequiredOf: (state) => eightball.callRequired(state, regrasDaPartida(state)),
  // O alvo do 8-Ball é o grupo inteiro, não uma bola. Apontar uma delas faria
  // o jogador acreditar numa obrigação que a regra não impõe.
  targetBallOf: () => null,
  contatoSeriaFalta: (state, bola) => eightball.wrongFirstBall(state, bola) !== null,
  resolve: (state, optionIndex, epoca) => {
    const pendente = state.pending
    if (!pendente) throw new Error('Não há decisão pendente.')

    /*
     * Validar contra as opções DESTA situação, não contra a lista canônica.
     *
     * `resolveChoice` manda tudo que não reconhece para o `default`, então um
     * índice fora da situação não era rejeitado: era silenciosamente
     * REINTERPRETADO. Quem perdeu a quebra forçava um re-rack e apagava a mesa
     * desfavorável — e como o índice vai gravado no replay, o verificador
     * reproduzia a mesma reinterpretação e confirmava o resultado adulterado em
     * vez de denunciá-lo. A entrada vem da rede: o cliente escolhe o byte.
     */
    const validas = OPCOES_POR_SITUACAO[pendente.kind]
    if (!validas?.includes(optionIndex)) {
      throw new Error(`Opção ${optionIndex} não é válida em "${pendente.kind}".`)
    }

    const escolha = BREAK_CHOICES[optionIndex]!
    // A época atravessa o re-rack: um rack novo numa partida antiga tem de
    // nascer com as regras daquela partida, e não com as de hoje.
    const novo = eightball.resolveChoice(state, escolha, epoca)

    // Rerack quando as regras rearmaram: mesa cheia de novo e por quebrar.
    const rerack = !novo.broken && novo.pocketed.length === 0

    // A 8 volta ao ponto de pé pela ESCOLHA feita, não por diferença entre as
    // listas de encaçapadas: com `respotEight` preenchido a 8 nunca chega a
    // entrar em `pocketed`, e a comparação passaria a não achar nada — a 8
    // ficaria fora da mesa física para sempre.
    const voltou = !rerack && pendente.kind === 'eight-on-break' && escolha === 'respot-eight'

    return { state: novo, rerack, respot: voltou ? [eightballTypes.EIGHT_BALL] : [] }
  },
  forfeit: (state, quem) => eightball.forfeit(state, quem),
  winnerOf: (state) => state.winner,
  // Menos bolas do próprio grupo na mesa. Com a mesa aberta ninguém tem grupo,
  // e aí não há do que medir — cai no desempate.
  standingWinner: (state) => {
    if (state.groups.open) return desempate(state.breaker)

    const faltam: [number, number] = [
      eightballTypes.remainingFor(state, 0).length,
      eightballTypes.remainingFor(state, 1).length,
    ]
    if (faltam[0] === faltam[1]) return desempate(state.breaker)
    return faltam[0] < faltam[1] ? 0 : 1
  },
  summarize: (state) => ({
    turn: state.turn,
    status: descreverEightball(state),
    statusCode: codigoEightball(state),
    score: null,
    onTable: bolasNaMesaEightball(state),
    ending: state.ending?.kind ?? null,
    finished: state.winner !== null,
    winner: state.winner,
    // Mesa aberta ainda não tem dono: devolver listas vazias diria "não falta
    // nada", que é o oposto da verdade.
    suas: state.groups.open
      ? null
      : [eightballTypes.remainingFor(state, 0), eightballTypes.remainingFor(state, 1)],
  }),
}

/** A mesma decisão de `descreverEightball`, em código. Uma muda, a outra muda. */
function codigoEightball(state: eightballTypes.MatchState): Status {
  if (state.winner !== null) return { code: 'venceu', params: { jogador: state.winner + 1 } }
  if (!state.broken) return { code: 'quebra' }
  if (state.groups.open) return { code: 'mesa_aberta' }

  const faltam = eightballTypes.remainingFor(state, state.turn)
  return faltam.length === 0 ? { code: 'na_bola_8' } : { code: 'faltam', params: { n: faltam.length } }
}

function descreverEightball(state: eightballTypes.MatchState): string {
  if (state.winner !== null) return `Jogador ${state.winner + 1} venceu`
  if (!state.broken) return 'Quebra'
  if (state.groups.open) return 'Mesa aberta'

  const faltam = eightballTypes.remainingFor(state, state.turn)
  return faltam.length === 0 ? 'Na bola 8' : `Faltam ${faltam.length}`
}

function bolasNaMesaEightball(state: eightballTypes.MatchState): number[] {
  const todas = [...eightballTypes.SOLIDS, eightballTypes.EIGHT_BALL, ...eightballTypes.STRIPES]
  return todas.filter((id) => !state.pocketed.includes(id))
}

// ------------------------------------------------------- sinuca brasileira

const sinucaMode: GameMode<
  sinucaTypes.SinucaState,
  sinucaTypes.SinucaOutcome,
  sinucaTypes.SinucaRuling
> = {
  info: GAME_MODE_INFO.sinuca,
  create: (breaker) => sinucaTypes.createSinucaMatch(breaker),
  judge: (state, outcome) => sinuca.judgeSinucaShot(state, outcome, ACTIVE_RULES.sinuca),
  play: (state, outcome) => sinuca.playSinucaShot(state, outcome, ACTIVE_RULES.sinuca),
  // A sinuca brasileira não abre escolha ao adversário em momento nenhum: toda
  // consequência de falta é automática (bola devolvida, pontos ao rival).
  pendingOf: () => null,
  resolve: () => {
    throw new Error('A sinuca brasileira não tem decisões pendentes.')
  },
  // A sinuca não tem a regra da cozinha: falta dá a bola na mão em qualquer
  // ponto livre.
  mesaQuebrada: (state) => state.broken,
  ballInHandOf: (state) => (state.ballInHand ? 'anywhere' : null),
  // A sinuca brasileira não tem bola declarada: a bola da vez é imposta pela
  // ordem crescente, então não há o que escolher.
  callRequiredOf: () => false,
  // A MESMA função que o julgamento usa, não uma cópia da conta. A duplicação
  // já existiu em três lugares e o resultado era a mesa destacando uma bola e o
  // texto do placar dizendo outra: quem acreditava no texto mirava errado e
  // levava os 7 pontos de penalidade.
  targetBallOf: (state) => sinuca.targetBall(state, ACTIVE_RULES.sinuca),
  /*
   * Na sinuca a bola da vez é imposta pela ordem crescente, então a pergunta
   * se responde comparando com o alvo — que é como a própria regra julga.
   * Sem alvo (mesa vazia), nada é falta.
   */
  contatoSeriaFalta: (state, bola) => {
    const alvo = sinuca.targetBall(state, ACTIVE_RULES.sinuca)
    return alvo !== null && bola !== alvo
  },
  forfeit: (state, quem) => sinuca.forfeitSinuca(state, quem),
  winnerOf: (state) => state.winner,
  // A sinuca já é decidida por pontos: quem está na frente está na frente.
  standingWinner: (state) => {
    if (state.score[0] === state.score[1]) return desempate(state.breaker)
    return state.score[0] > state.score[1] ? 0 : 1
  },
  summarize: (state) => {
    const alvo = sinuca.targetBall(state)
    return {
      turn: state.turn,
      status:
        state.winner !== null
          ? `Jogador ${state.winner + 1} venceu`
          : alvo === null
            ? 'Mesa vazia'
            : `Bola da vez: ${sinucaTypes.BALL_NAMES[alvo] ?? alvo}`,
      statusCode:
        state.winner !== null
          ? { code: 'venceu', params: { jogador: state.winner + 1 } }
          : alvo === null
            ? { code: 'mesa_vazia' }
            : { code: 'bola_da_vez', params: { bola: alvo } },
      score: state.score,
      onTable: state.onTable,
      // Pelo FIM registrado, não pelo vencedor. Hoje os dois andam juntos — a
      // mesa vazia empatada vai para a decisão na preta em vez de encerrar sem
      // vencedor —, mas olhar o `ending` é o que descreve a partida ter parado,
      // e é ele que o servidor usa para parar de aceitar tacadas.
      finished: state.ending !== null,
      winner: state.winner,
      // A sinuca não divide a mesa em grupos: a bola da vez vale para os dois,
      // e é o `targetBall` que a informa.
      ending: state.ending?.kind ?? null,
      suas: null,
    }
  },
}

// -------------------------------------------------------------- registro

const REGISTRO: Record<GameModeId, GameMode<never, never, never>> = {
  eightball: eightballMode as GameMode<never, never, never>,
  sinuca: sinucaMode as GameMode<never, never, never>,
}

/**
 * Busca a modalidade pelo id.
 *
 * Lança em id desconhecido em vez de cair para um padrão: uma sala com
 * modalidade inválida é bug, e resolver silenciosamente faria os dois
 * jogadores jogarem jogos diferentes.
 */
export function getGameMode(id: GameModeId): GameMode<never, never, never> {
  const modo = REGISTRO[id]
  if (!modo) throw new Error(`Modalidade desconhecida: ${id}`)
  return modo
}

export const isGameModeId = (value: string): value is GameModeId =>
  (GAME_MODES as readonly string[]).includes(value)
